import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const DELAY_MS = 300
const GAP = 6
const EDGE = 8

// Native `title` tooltips look nothing like IconTip; this shows every title in
// the same styled bubble instead. Mouse only, like IconTip.
export function TitleTips() {
  const [tip, setTip] = useState<{ text: string; rect: DOMRect } | null>(null)
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let current: Element | null = null
    let timer: ReturnType<typeof setTimeout> | undefined
    let lastHide = 0
    const hide = () => {
      clearTimeout(timer)
      if (current) lastHide = Date.now()
      current = null
      setTip(null)
    }
    const onOver = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || !(e.target instanceof Element)) return
      const el = e.target.closest('[title],[data-tip]')
      if (el === current) return
      hide()
      if (!el) return
      const title = el.getAttribute('title')
      // Re-read each time: React writes `title` back when its value changes.
      if (title !== null) {
        el.removeAttribute('title')
        if (title) el.setAttribute('data-tip', title)
        else el.removeAttribute('data-tip')
        if (title && !el.hasAttribute('aria-label') && !el.textContent?.trim())
          el.setAttribute('aria-label', title)
      }
      const text = el.getAttribute('data-tip')
      if (!text) return
      current = el
      const show = () => setTip({ text, rect: el.getBoundingClientRect() })
      if (Date.now() - lastHide < DELAY_MS) show()
      else timer = setTimeout(show, DELAY_MS)
    }
    const onOut = (e: PointerEvent) => {
      if (current && !(e.relatedTarget instanceof Node && current.contains(e.relatedTarget))) hide()
    }
    document.addEventListener('pointerover', onOver)
    document.addEventListener('pointerout', onOut)
    document.addEventListener('pointerdown', hide, true)
    document.addEventListener('keydown', hide, true)
    window.addEventListener('scroll', hide, true)
    window.addEventListener('blur', hide)
    return () => {
      hide()
      document.removeEventListener('pointerover', onOver)
      document.removeEventListener('pointerout', onOut)
      document.removeEventListener('pointerdown', hide, true)
      document.removeEventListener('keydown', hide, true)
      window.removeEventListener('scroll', hide, true)
      window.removeEventListener('blur', hide)
    }
  }, [])

  useLayoutEffect(() => {
    const box = ref.current
    if (!tip || !box) return setPos(null)
    const { width, height } = box.getBoundingClientRect()
    const { rect } = tip
    const below = rect.bottom + GAP
    const top = below + height <= window.innerHeight - EDGE ? below : rect.top - GAP - height
    const left = Math.min(
      Math.max(EDGE, rect.left + rect.width / 2 - width / 2),
      window.innerWidth - EDGE - width,
    )
    setPos({ left, top })
  }, [tip])

  if (!tip) return null
  return createPortal(
    <div
      ref={ref}
      role="tooltip"
      className="pointer-events-none fixed z-[100] w-fit max-w-xs rounded-md bg-foreground px-2.5 py-1.5 text-xs font-medium text-background shadow-md animate-in fade-in-0 zoom-in-95"
      style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: 'hidden' }}
    >
      {tip.text}
    </div>,
    document.body,
  )
}
